import React, { useState, useEffect, useRef } from 'react';
import {
  Truck,
  Shield,
  MapPin,
  Bell,
  Smartphone,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Phone,
  Mail,
  TrendingUp,
  Zap,
  AlertCircle,
  MessageCircle,
  Building2,
  Navigation,
  Clock,
  CheckCircle,
  FileCheck,
  Briefcase,
  BarChart3,
  Package,
  Wallet,
} from 'lucide-react';
import AnimatedBackground from '../components/AnimatedBackground';
import TruckResults from '../components/TruckResults';
import TruckTestimonials from '../components/TruckTestimonials';

const useScrollAnimation = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return { ref, isVisible };
};

const Trucks: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const services = [
    {
      icon: Truck,
      title: 'FTL',
      subtitle: 'Full Truck Load',
      description: 'Run dedicated long-haul movements with clearer dispatch planning, live route visibility, and tighter trip-to-bill control.',
      color: 'bg-blue-50 text-blue-600',
    },
    {
      icon: Package,
      title: 'PTL',
      subtitle: 'Part Truck Load',
      description: 'Coordinate consolidated loads, multi-drop execution, and shared capacity with less manual follow-up.',
      color: 'bg-orange-50 text-orange-600',
    },
    {
      icon: Zap,
      title: 'On Demand',
      subtitle: 'Spot & Emergency Loads',
      description: 'Respond quickly to urgent bookings and breakdown-driven replacements without disrupting dispatch workflows.',
      color: 'bg-green-50 text-green-600',
    },
    {
      icon: Briefcase,
      title: 'Lease / Rental',
      subtitle: 'Vehicle Asset Support',
      description: 'Manage leased and rental vehicles with the same compliance, billing, and service monitoring used for active fleet operations.',
      color: 'bg-purple-50 text-purple-600',
    },
  ];

  const features = [
    {
      icon: MapPin,
      title: 'Live Fleet Visibility',
      description: 'Track trucks, trips, ETAs, and route deviations in real time across active freight operations.',
      color: 'from-blue-500 to-blue-600',
    },
    {
      icon: Navigation,
      title: 'Dispatch & Load Planning',
      description: 'Plan loads, assign vehicles, manage routes, and keep dispatch teams aligned across FTL, PTL, and spot jobs.',
      color: 'from-orange-500 to-orange-600',
    },
    {
      icon: Wallet,
      title: 'Dynamic Pricing & Billing',
      description: 'Apply pricing rules by trip, distance, overtime, or service type and turn trip activity into faster invoicing.',
      color: 'from-green-500 to-green-600',
    },
    {
      icon: Smartphone,
      title: 'Driver & Mobile Workflows',
      description: 'Support driver assignments, status updates, route actions, and admin alerts through mobile-first workflows.',
      color: 'from-purple-500 to-purple-600',
    },
    {
      icon: FileCheck,
      title: 'Compliance & Inventory',
      description: 'Track insurance, registration, fitness, pollution, and operational readiness so vehicles stay road-ready.',
      color: 'from-pink-500 to-pink-600',
    },
    {
      icon: BarChart3,
      title: 'MIS & Operations Analytics',
      description: 'Review utilization, billing speed, route trends, and service performance through cleaner operational reporting.',
      color: 'from-red-500 to-red-600',
    },
  ];

  const faqs = [
    {
      q: 'Can GaadiMatic handle both FTL and PTL workflows?',
      a: 'Yes. The Trucks product is positioned to support dedicated FTL movements as well as PTL operations that need tighter planning, tracking, and operational visibility.',
    },
    {
      q: 'Is this suitable for operators managing leased or rental vehicles?',
      a: 'Yes. Lease and rental operations can be managed within the same freight workflow so teams keep one operational view across owned and managed fleets.',
    },
    {
      q: 'How does the platform help dispatch teams?',
      a: 'Dispatch teams get better trip visibility, cleaner route coordination, faster exception handling, and less manual follow-up during active operations.',
    },
    {
      q: 'Can we track billing and trip reconciliation?',
      a: 'Yes. The product is designed to improve billing readiness with cleaner trip records, pricing visibility, reconciliation support, and clearer month-end workflows.',
    },
    {
      q: 'Does the product help with compliance?',
      a: 'Yes. It supports a more centralized view of vehicle and operational compliance so fleet operators can act before issues disrupt service.',
    },
    {
      q: 'Do drivers and admins get mobile workflows as well?',
      a: 'Yes. The platform is designed around both office operations and in-field execution, including driver updates, admin visibility, alerts, and trip status management.',
    },
    {
      q: 'Who is this page built for?',
      a: 'The Trucks offering is built for fleet operators, transport companies, dispatch teams, and logistics businesses that need better visibility and control over freight execution.',
    },
  ];

  const FeatureCard: React.FC<{ feature: (typeof features)[0]; index: number }> = ({ feature, index }) => {
    const { ref, isVisible } = useScrollAnimation();
    const Icon = feature.icon;

    return (
      <div
        ref={ref}
        className={`
          group relative overflow-hidden rounded-2xl bg-white border border-gray-100 p-6
          hover:shadow-xl hover:shadow-primary-500/10 hover:-translate-y-1
          transition-all duration-500 ease-out
          ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
        `}
        style={{ transitionDelay: `${index * 100}ms` }}
      >
        <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />

        <div
          className={`
            relative w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color}
            flex items-center justify-center mb-4
            group-hover:scale-110 group-hover:rotate-3
            transition-all duration-500 shadow-lg
          `}
        >
          <Icon className="w-7 h-7 text-white" />
        </div>

        <h3 className="relative text-xl font-bold text-navy-950 mb-2 group-hover:text-primary-600 transition-colors">
          {feature.title}
        </h3>
        <p className="relative text-gray-600 leading-relaxed">{feature.description}</p>

        <div className="relative mt-4 flex items-center gap-2 text-primary-600 font-semibold opacity-0 group-hover:opacity-100 transform translate-x-[-10px] group-hover:translate-x-0 transition-all duration-300">
          <span>Learn more</span>
          <ArrowRight className="w-4 h-4" />
        </div>
      </div>
    );
  };

  const DispatchMockup = () => (
    <div className="bg-white rounded-3xl shadow-sm overflow-hidden border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-lg font-bold">Dispatch Board</div>
          <div className="text-sm text-gray-500">Live Trip Assignment</div>
        </div>
        <div className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold">
          Peak Dispatch
        </div>
      </div>

      <div className="space-y-3">
        {[
          { route: 'BLR → HYD', detail: 'FTL • 12 tons', status: 'Assigned' },
          { route: 'HYD → VJA', detail: 'PTL • 4 drops', status: 'En route' },
          { route: 'BLR → CHE', detail: 'Spot booking', status: 'Confirmed' },
        ].map((item, i) => (
          <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
            <div>
              <div className="text-sm font-semibold text-navy-950">{item.route}</div>
              <div className="text-xs text-gray-500">{item.detail}</div>
            </div>
            <div className="text-xs font-medium text-primary-600">{item.status}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-4 mt-6">
        {[
          { label: 'Active Trips', value: '34', change: '+6' },
          { label: 'Delayed', value: '3', change: 'Alert' },
          { label: 'Available Trucks', value: '11', change: 'Live' },
        ].map((stat, i) => (
          <div key={i} className="bg-gray-50 rounded-xl p-3">
            <div className="text-2xl font-bold text-navy-950">{stat.value}</div>
            <div className="text-xs text-gray-500">{stat.label}</div>
            <div className="text-xs text-green-600 mt-1">{stat.change}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const OperatorMockup = () => (
    <div className="bg-white rounded-3xl shadow-sm overflow-hidden border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-lg font-bold">Fleet Control Tower</div>
          <div className="text-sm text-gray-500">Route and Vehicle Status</div>
        </div>
        <div className="flex gap-2">
          <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">28 Active</div>
          <div className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold">4 Exceptions</div>
        </div>
      </div>

      <div className="h-40 bg-gradient-to-br from-green-50 to-blue-50 rounded-xl relative overflow-hidden mb-5">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="grid grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className={`w-3 h-3 rounded-full ${i % 3 === 0 ? 'bg-green-500 animate-pulse' : 'bg-primary-600'}`} />
            ))}
          </div>
        </div>
        <div className="absolute bottom-3 left-3 bg-white rounded-lg px-3 py-1 shadow-sm text-xs font-medium">
          Active vehicles in transit
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="text-xs text-gray-500 mb-1">Fleet Utilization</div>
          <div className="text-2xl font-bold text-navy-950">94%</div>
        </div>
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="text-xs text-gray-500 mb-1">On-Time Trips</div>
          <div className="text-2xl font-bold text-navy-950">91%</div>
        </div>
      </div>
    </div>
  );

  const BillingMockup = () => (
    <div className="bg-white rounded-3xl shadow-sm overflow-hidden border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-lg font-bold">Billing & Reconciliation</div>
          <div className="text-sm text-gray-500">Trip to Invoice Workflow</div>
        </div>
        <div className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold">
          Month End
        </div>
      </div>

      <div className="space-y-3 mb-5">
        {[
          { title: 'Verified Trips', value: '182' },
          { title: 'Pending Review', value: '14' },
          { title: 'Invoices Ready', value: '27' },
        ].map((item, i) => (
          <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
            <div className="text-sm font-medium text-navy-950">{item.title}</div>
            <div className="text-sm font-bold text-primary-600">{item.value}</div>
          </div>
        ))}
      </div>

      <div className="bg-gray-50 rounded-xl p-4">
        <div className="text-xs text-gray-500 mb-2">Collections Readiness</div>
        <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full w-4/5 bg-primary-600 rounded-full" />
        </div>
        <div className="text-xs text-green-600 mt-2">80% trips invoice-ready</div>
      </div>
    </div>
  );

  const DriverAppMockup = () => (
    <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-200">
      <div className="bg-gray-900 p-2">
        <div className="bg-white rounded-2xl overflow-hidden">
          <div className="bg-primary-600 px-4 py-3 flex items-center justify-between">
            <div className="text-white font-semibold">Driver Trip App</div>
            <div className="w-8 h-8 bg-white/20 rounded-full" />
          </div>

          <div className="p-4 space-y-4">
            <div className="bg-blue-50 rounded-xl p-4">
              <div className="text-xs text-gray-500 mb-1">Current Trip</div>
              <div className="text-sm font-semibold text-navy-950">HYD to VJA • PTL</div>
              <div className="text-xs text-primary-600 mt-1">3 deliveries pending</div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Start Trip', active: true },
                { label: 'Upload Docs', active: false },
                { label: 'Update Status', active: false },
                { label: 'Call Dispatch', active: false },
              ].map((item, i) => (
                <div
                  key={i}
                  className={`rounded-xl p-3 text-center text-sm font-medium ${
                    item.active ? 'bg-primary-600 text-white' : 'bg-gray-50 text-gray-700'
                  }`}
                >
                  {item.label}
                </div>
              ))}
            </div>

            <div className="space-y-3">
              {[
                { time: '08:10 AM', event: 'Driver assigned', status: 'completed' },
                { time: '08:32 AM', event: 'Trip started', status: 'completed' },
                { time: '10:15 AM', event: 'Status updated from route', status: 'current' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      item.status === 'completed' ? 'bg-green-500' : 'bg-primary-600 animate-pulse'
                    }`}
                  />
                  <div className="flex-1">
                    <div className="text-xs text-gray-500">{item.time}</div>
                    <div className="text-sm font-medium">{item.event}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <AnimatedBackground />

      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden bg-gray-50">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50/50 via-transparent to-accent-orange/5" />
        <div className="container-main relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-100 rounded-full mb-6 animate-fade-in">
              <Truck className="w-4 h-4 text-primary-600" />
              <span className="text-sm font-medium text-primary-700">Built for Fleet Operators & Transport Companies</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy-950 leading-tight mb-6">
              Run FTL, PTL, Spot, and Lease Operations <br />
              with <span className="text-primary-600">Real Daily Control.</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-10">
              Built for Indian freight operations, GaadiMatic helps transport businesses replace fragmented calls,
              spreadsheets, and manual follow-up with one operating layer for dispatch, compliance, driver workflows,
              billing readiness, and fleet performance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#contact" className="btn-primary group">
                Request Demo
              </a>
              <a href="/pricing" className="btn-secondary">
                View Pricing
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <TruckResults />

      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-6">
                The Challenge with Freight Operations
              </h2>
              <div className="space-y-4">
                {[
                  { icon: AlertCircle, text: 'Dispatch teams struggle when trips, trucks, spot bookings, and exceptions are tracked in multiple places' },
                  { icon: Clock, text: 'Operators lose time chasing status from drivers and vendors during active loads and route changes' },
                  { icon: MessageCircle, text: 'Billing gets delayed when trip closure, proof, pricing, and operational data are incomplete or scattered' },
                  { icon: TrendingUp, text: 'Margins shrink when utilization, empty runs, compliance renewals, and service quality are not visible daily' },
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-red-50 transition-colors">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-red-500" />
                    </div>
                    <p className="text-gray-700">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-primary-50 to-accent-orange/5 rounded-2xl p-8 sm:p-12">
                <div className="text-center">
                  <div className="w-20 h-20 bg-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Truck className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-navy-950 mb-4">Trucking Operations, Simplified</h3>
                  <div className="space-y-3">
                    {[
                      'Track active trips, route progress, and fleet movement in real time',
                      'Manage FTL, PTL, spot loads, and leased vehicles in one system',
                      'Improve pricing, billing readiness, and trip-to-invoice workflows',
                      'Keep compliance, breakdown alerts, utilization, and performance visible from one layer',
                    ].map((item, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700 text-left">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Four Freight Models, One Operating Platform
            </h2>
            <p className="text-lg text-gray-600">
              Cover your major trucking workflows in one platform instead of splitting dispatch, billing, and compliance across separate tools.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div key={index} className="card p-6">
                <div className={`w-14 h-14 ${service.color} rounded-xl flex items-center justify-center mb-5`}>
                  <service.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-navy-950 mb-1">{service.title}</h3>
                <p className="text-sm font-medium text-primary-600 mb-3">{service.subtitle}</p>
                <p className="text-sm text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Core Capabilities for Freight Execution
            </h2>
            <p className="text-lg text-gray-600">
              Give your teams stronger control across trip planning, pricing, drivers, vehicles, compliance, and billing.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <FeatureCard key={index} feature={feature} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-sm text-gray-500 mb-4 uppercase tracking-widest font-medium">Built for Freight Teams</p>
            <h2 className="text-5xl lg:text-6xl font-bold text-navy-950 font-heading leading-[0.9] mb-6">
              POWERING<br />
              <span className="text-primary-600">EVERYDAY EXECUTION</span>
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Purpose-built workflows for dispatch, drivers, fleet operators, and finance teams help freight businesses move with more confidence.
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50 overflow-hidden">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-100 to-blue-50 rounded-3xl blur-2xl opacity-70" />
                <div className="relative bg-white rounded-3xl shadow-sm p-0 border border-gray-100">
                  <DispatchMockup />
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full mb-6">
                <Navigation className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">For Dispatch Teams</span>
              </div>
              <h3 className="text-4xl lg:text-5xl font-bold text-navy-950 font-heading mb-4">
                Keep Trips Moving Without Constant Firefighting
              </h3>
              <p className="text-xl text-gray-600 mb-8">
                Dispatch teams get one space to assign loads, manage trip changes, handle spot requirements, and keep execution visible during active movement.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: Navigation, text: 'Plan and assign trips faster across active routes and service types' },
                  { icon: Bell, text: 'Respond faster to delays, breakdowns, and operational exceptions' },
                  { icon: Clock, text: 'Reduce manual coordination across trips, drivers, and vendors' },
                  { icon: MessageCircle, text: 'Keep internal teams aligned with clearer live trip status' },
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-xl shadow-sm">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-blue-600" />
                      </div>
                      <span className="text-gray-700 font-medium text-sm">{item.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white overflow-hidden">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full mb-6">
                <Truck className="w-4 h-4 text-orange-600" />
                <span className="text-sm font-medium text-orange-700">For Fleet Operators</span>
              </div>
              <h3 className="text-4xl lg:text-5xl font-bold text-navy-950 font-heading mb-4">
                One Control Tower for Active Fleet Operations
              </h3>
              <p className="text-xl text-gray-600 mb-8">
                See which trucks are moving, which routes need attention, and where compliance, utilization, or service quality needs intervention.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: MapPin, text: 'Track fleet movement and route progress in real time' },
                  { icon: Shield, text: 'Keep compliance, road-readiness, and document renewals visible' },
                  { icon: TrendingUp, text: 'Improve utilization and reduce non-productive movement' },
                  { icon: Building2, text: 'Manage multi-route, multi-team, and multi-service freight execution' },
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                      <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-orange-600" />
                      </div>
                      <span className="text-gray-700 font-medium text-sm">{item.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-orange-100 to-orange-50 rounded-3xl blur-2xl opacity-70" />
                <div className="relative bg-white rounded-3xl shadow-sm p-0 border border-gray-100">
                  <OperatorMockup />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white overflow-hidden">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full mb-6">
                <Smartphone className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium text-purple-700">For Drivers & Mobile Execution</span>
              </div>
              <h3 className="text-4xl lg:text-5xl font-bold text-navy-950 font-heading mb-4">
                Keep Drivers Connected to the Operation
              </h3>
              <p className="text-xl text-gray-600 mb-8">
                Support field execution with mobile workflows for trip updates, route actions, dispatch coordination, and document sharing.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: Smartphone, text: 'Receive trip assignments and route-level actions on mobile' },
                  { icon: Bell, text: 'Share status updates quickly during active movement' },
                  { icon: FileCheck, text: 'Upload operational or document inputs from the field' },
                  { icon: Phone, text: 'Stay connected to dispatch during changes and exceptions' },
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                      <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-purple-600" />
                      </div>
                      <span className="text-gray-700 font-medium text-sm">{item.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-purple-100 to-purple-50 rounded-3xl blur-2xl opacity-70" />
                <div className="relative bg-white rounded-3xl shadow-sm p-0 border border-gray-100">
                  <DriverAppMockup />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50 overflow-hidden">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-green-100 to-green-50 rounded-3xl blur-2xl opacity-70" />
                <div className="relative bg-white rounded-3xl shadow-sm p-0 border border-gray-100">
                  <BillingMockup />
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 rounded-full mb-6">
                <Wallet className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-700">For Billing & Finance Teams</span>
              </div>
              <h3 className="text-4xl lg:text-5xl font-bold text-navy-950 font-heading mb-4">
                Move Faster from Trip to Invoice
              </h3>
              <p className="text-xl text-gray-600 mb-8">
                Turn verified trip activity into cleaner billing workflows so finance teams spend less time chasing operational data and more time closing faster.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: FileCheck, text: 'Improve billing readiness with cleaner trip records and proof' },
                  { icon: BarChart3, text: 'Track reconciliation, MIS reports, and month-end status more easily' },
                  { icon: Zap, text: 'Reduce delays caused by fragmented trip inputs' },
                  { icon: TrendingUp, text: 'Get better visibility into freight economics and pricing performance' },
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-xl shadow-sm">
                      <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-green-600" />
                      </div>
                      <span className="text-gray-700 font-medium text-sm">{item.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              How the Trucks Platform Works
            </h2>
            <p className="text-lg text-gray-600">
              Bring planning, dispatch, field execution, compliance, and billing into a repeatable freight workflow.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { step: '1', title: 'Set Up', desc: 'Add vehicles, drivers, pricing rules, documents, and operational workflows' },
              { step: '2', title: 'Plan', desc: 'Create FTL, PTL, spot, or lease movements with the right pricing and allocation' },
              { step: '3', title: 'Dispatch', desc: 'Assign trucks and launch trips with cleaner coordination across office and field teams' },
              { step: '4', title: 'Monitor', desc: 'Track route progress, breakdowns, exceptions, and active fleet status live' },
              { step: '5', title: 'Reconcile', desc: 'Turn trip activity into reports, billing validation, and performance review' },
            ].map((item, index) => (
              <div key={index} className="text-center group">
                <div className="relative inline-block">
                  <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-lg group-hover:scale-110 transition-transform">
                    {item.step}
                  </div>
                  {index < 4 && <div className="hidden lg:block absolute top-1/2 left-full w-full h-0.5 bg-primary-200" />}
                </div>
                <h3 className="font-bold text-navy-950 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Everything You Need to Run Freight Better
            </h2>
            <p className="text-lg text-gray-600">
              A complete operating layer for trucking businesses, organized around execution, control, and commercial outcomes.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <Truck className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-navy-950">Execution Control</h3>
              </div>
              {[
                { name: 'Live Trip Status', desc: 'Know what is moving, delayed, or completed' },
                { name: 'Dispatch Coordination', desc: 'Reduce daily follow-up overhead' },
                { name: 'Breakdown & Exception Alerts', desc: 'Respond faster to delays, route issues, and urgent changes' },
                { name: 'Multi-Service Support', desc: 'Handle FTL, PTL, and spot loads together' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-white rounded-xl hover:shadow-md transition-all border border-transparent hover:border-gray-100">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-navy-950 text-sm">{item.name}</h4>
                    <p className="text-xs text-gray-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-navy-950">Fleet Readiness</h3>
              </div>
              {[
                { name: 'Vehicle Inventory', desc: 'Keep documents, asset readiness, and renewal status visible' },
                { name: 'Driver Allocation', desc: 'Assign and monitor operational availability across active trips' },
                { name: 'Route Monitoring', desc: 'See progress and deviations more clearly' },
                { name: 'Role-Based Security', desc: 'Support cleaner control and accountability across teams' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-white rounded-xl hover:shadow-md transition-all border border-transparent hover:border-gray-100">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-navy-950 text-sm">{item.name}</h4>
                    <p className="text-xs text-gray-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-navy-950">Commercial Control</h3>
              </div>
              {[
                { name: 'Trip-to-Bill Flow', desc: 'Support faster invoice readiness' },
                { name: 'Dynamic Pricing', desc: 'Apply pricing logic by trip, service type, distance, or overtime' },
                { name: 'Performance Analytics', desc: 'Compare routes, trips, and execution trends' },
                { name: 'Contract Management', desc: 'Track pricing patterns and commercial service commitments' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-white rounded-xl hover:shadow-md transition-all border border-transparent hover:border-gray-100">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-navy-950 text-sm">{item.name}</h4>
                    <p className="text-xs text-gray-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white border-y border-gray-100">
        <div className="container-main">
          <div className="text-center mb-10">
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Built for Freight Businesses of Different Shapes</p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { title: 'Fleet Operators', desc: 'Track trips, drivers, and vehicles with stronger daily control' },
              { title: 'Transport Companies', desc: 'Run dispatch, pricing, and billing with less manual effort' },
              { title: 'Regional Carriers', desc: 'Manage route-heavy operations across cities and corridors' },
              { title: 'Enterprise Logistics Teams', desc: 'Bring operational visibility and accountability to outsourced freight movement' },
            ].map((useCase, index) => (
              <div key={index} className="card p-6 text-center">
                <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="font-bold text-navy-950 mb-2">{useCase.title}</h3>
                <p className="text-sm text-gray-600">{useCase.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TruckTestimonials />

      <section className="section-padding bg-gray-50">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to know about the Trucks offering.
            </p>
          </div>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-navy-950">{faq.q}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Flexible Rollout for Freight Operations
            </h2>
            <p className="text-lg text-gray-600">
              Start with the workflows that matter most and expand across service types, routes, drivers, and operational teams.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                name: 'Core Dispatch',
                description: 'For teams focused on trip visibility and live operational coordination',
                features: ['Trip monitoring', 'Dispatch workflows', 'Vehicle status visibility', 'Standard reporting'],
              },
              {
                name: 'Growth Operations',
                description: 'For operators managing multiple service types and larger route volumes',
                features: ['Everything in Core', 'FTL + PTL support', 'Billing workflows', 'Compliance visibility'],
                popular: true,
              },
              {
                name: 'Enterprise Freight',
                description: 'For large operators and logistics businesses needing deeper control',
                features: ['Everything in Growth', 'Custom rollout support', 'Advanced analytics', 'Dedicated onboarding'],
              },
            ].map((plan, index) => (
              <div key={index} className={`card p-6 relative ${plan.popular ? 'border-2 border-primary-500' : ''}`}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary-600 text-white text-xs font-semibold px-3 py-1 rounded-full">Most Popular</span>
                  </div>
                )}
                <h3 className="text-xl font-bold text-navy-950 mb-2">{plan.name}</h3>
                <p className="text-gray-600 text-sm mb-6">{plan.description}</p>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button className={`w-full ${plan.popular ? 'btn-primary' : 'btn-secondary'}`}>Request Demo</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="section-padding bg-navy-950">
        <div className="container-main">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Modernize Your Truck Operations?
            </h2>
            <p className="text-lg text-gray-400 mb-10">
              Bring dispatch, live fleet visibility, compliance, and billing readiness into one freight operating platform.
              Start with a walkthrough tailored to your service mix and daily workflow.
            </p>

            <div className="grid sm:grid-cols-3 gap-6 mb-10">
              <div className="bg-white/10 rounded-xl p-6 text-center hover:bg-white/20 transition-colors">
                <Phone className="w-8 h-8 text-primary-400 mx-auto mb-3" />
                <div className="text-white font-medium mb-1">Call Us</div>
                <div className="text-gray-400 text-sm">+91 99165 00454</div>
              </div>
              <div className="bg-white/10 rounded-xl p-6 text-center hover:bg-white/20 transition-colors">
                <Mail className="w-8 h-8 text-primary-400 mx-auto mb-3" />
                <div className="text-white font-medium mb-1">Email Us</div>
                <div className="text-gray-400 text-sm">info@gaadimatic.com</div>
              </div>
              <div className="bg-white/10 rounded-xl p-6 text-center hover:bg-white/20 transition-colors">
                <Clock className="w-8 h-8 text-primary-400 mx-auto mb-3" />
                <div className="text-white font-medium mb-1">Go-Live Support</div>
                <div className="text-gray-400 text-sm">Onboarding for freight operations</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#contact" className="btn-primary text-lg px-10 inline-flex items-center justify-center">
                Request Demo
              </a>
              <a href="/pricing" className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white border-2 border-white/30 rounded-lg transition-all duration-200 hover:bg-white/10">
                View Pricing
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Trucks;
